import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { Heading, Text, Button } from '@shinkansen/sakura'
import { BrandLockup } from '~/components/BrandLockup'
import { resetPassword } from '~/auth/auth-client'
import styles from './auth.module.css'

const MIN_PASSWORD_LENGTH = 8

// Map auth-server error codes to human-friendly copy.
function mapResetError(error: string): string {
  switch (error) {
    case 'invalid_token':
    case 'token_expired':
      return 'This reset link is invalid or has expired. Please request a new one.'
    default:
      return 'Unable to reset your password. Please try again.'
  }
}

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const { token } = useSearch({ from: '/reset-password' })

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (isSubmitting) return

    setError('')

    if (!token) {
      setError('This reset link is invalid or has expired. Please request a new one.')
      return
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`)
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsSubmitting(true)
    try {
      const result = await resetPassword(token, password)
      // Success is signalled either by `true` or an `AuthResult` with `ok: true`.
      if (result === true || (result !== false && result.ok)) {
        await navigate({ to: '/login', search: { reset: 'success' } })
        return
      }
      // `false` means the request failed without a server-provided code.
      setError(result === false ? mapResetError('') : mapResetError(result.error))
    } catch {
      setError('Unable to reset your password. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={styles.page}>
      <span className={styles.bgNumber}>03</span>

      <div className={styles.container}>
        <header className={styles.header}>
          <BrandLockup variant="auth" meta="ACCOUNT RECOVERY" />
        </header>

        <main className={styles.main}>
          <Heading level="h2" size="xl" className={styles.formTitle}>
            SET NEW PASSWORD
          </Heading>

          {!token ? (
            <>
              <div className={styles.error}>
                This reset link is invalid or has expired. Please request a new one.
              </div>
              <div className={styles.backToLogin}>
                <Link className={styles.backLink} to="/forgot-password">
                  REQUEST NEW LINK
                </Link>
              </div>
            </>
          ) : (
            <>
              <Text variant="muted" size="sm" className={styles.instructions}>
                Choose a new password for your account, then sign in.
              </Text>

              {error && <div className={styles.error}>{error}</div>}

              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="password">
                    NEW PASSWORD
                  </label>
                  <input
                    className={styles.input}
                    id="password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    minLength={MIN_PASSWORD_LENGTH}
                    autoFocus
                    required
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="confirmPassword">
                    CONFIRM PASSWORD
                  </label>
                  <input
                    className={styles.input}
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  variant="accent"
                  size="lg"
                  width="full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'SAVING...' : 'SAVE & SIGN IN'}
                </Button>
              </form>

              <div className={styles.backToLogin}>
                <Link className={styles.backLink} to="/login" search={{ reset: undefined }}>
                  BACK TO LOGIN
                </Link>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  )
}
